'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDateForUser } from '@/utils/formatters';
import type { GridViewMode } from './grid-model';

interface GridViewSwitchProps {
  mode: GridViewMode;
  onModeChange: (mode: GridViewMode) => void;
  /** Sorted, from the grid model. Empty disables the day mode entirely. */
  dates: string[];
  selectedDate: string;
  onSelectedDateChange: (date: string) => void;
}

/** Week/day toggle plus, in day mode, the previous/next day navigation. */
export function GridViewSwitch({
  mode,
  onModeChange,
  dates,
  selectedDate,
  onSelectedDateChange,
}: GridViewSwitchProps) {
  const index = dates.indexOf(selectedDate);

  const step = (delta: number) => {
    const next = dates[index + delta];
    if (next) onSelectedDateChange(next);
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Radix `Tabs` without any `TabsContent`: the panels are rendered by the parent, which has
          to keep them mutually exclusive for dnd-kit's sake anyway. Used purely as the segmented
          control, the same way the participants card does it — that buys the app's own active-tab
          styling plus arrow-key navigation and the tablist roles for free. */}
      <Tabs value={mode} onValueChange={(value) => onModeChange(value as GridViewMode)}>
        <TabsList>
          <TabsTrigger value="week">Semana</TabsTrigger>
          <TabsTrigger value="day" disabled={dates.length === 0}>
            Día
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {mode === 'day' && dates.length > 0 && (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => step(-1)}
            disabled={index <= 0}
            aria-label="Día anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[140px] text-center text-sm font-medium">
            {formatDateForUser(selectedDate, 'EEE dd/MM')}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => step(1)}
            disabled={index === -1 || index >= dates.length - 1}
            aria-label="Día siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
